import { Injectable } from '@angular/core';
import { Dropbox, files } from 'dropbox';
import { LocalStorage } from '../../auth/local.storage';
import { DropboxAuthInfo, DropboxAuthService } from './dropbox-auth.service';
import { CloudExceptionEnum } from '../models/exceptions';
import { CloudRecentFilesService } from '../cloud-recent-files.service';
import { CloudServiceEnum } from '../enums';

export enum DropboxUploadMode {
  Add = 'add',
  Overwrite = 'overwrite',
  Update = 'update'
}

@Injectable({
  providedIn: 'root'
})
export class DropboxApiService {
  private DROPBOX_INFO_KEY: string = 'DROPBOX_INFO_KEY';
  private dbx: Dropbox;
  private currentToken: string | null = null;

  constructor(
    private readonly authSvc: DropboxAuthService,
    private readonly cloudRecentFilesService: CloudRecentFilesService,
    private localStorage: LocalStorage
  ) { }

  async getFiles(inFolder: string): Promise<files.ListFolderResult> {
    const dropbox = await this.getDropboxInstance();

    if (!dropbox) {
      return Promise.reject(CloudExceptionEnum.fileRetrieval);
    }

    const args: files.ListFolderArg = {
      path: inFolder,
      recursive: false,
      include_media_info: true,
      include_deleted: false,
      include_has_explicit_shared_members: true,
      include_mounted_folders: true,
    };

    return (await dropbox.filesListFolder(args)).result;
  }

  async newFolder(folderId: string, autorename: boolean): Promise<files.CreateFolderResult> {
    const args: files.CreateFolderArg = {
      path: folderId,
      autorename
    };

    const dropbox = await this.getDropboxInstance();
    return (await dropbox.filesCreateFolderV2(args)).result;
  }

  async copyFile(fromPath: string, toPath: string, autorename: boolean): Promise<files.RelocationResult> {
    const args: files.RelocationArg = {
      from_path: fromPath,
      to_path: toPath,
      autorename
    };

    const dropbox = await this.getDropboxInstance();
    return (await dropbox.filesCopyV2(args)).result;
  }

  async moveFile(fromPath: string, toPath: string, autorename: boolean): Promise<files.RelocationResult> {
    const args: files.RelocationArg = {
      from_path: fromPath,
      to_path: toPath,
      autorename
    };

    const dropbox = await this.getDropboxInstance();
    return (await dropbox.filesMoveV2(args)).result;
  }

  async deleteFile(filePath: string): Promise<files.DeleteResult> {
    const args: files.DeleteArg = {
      path: filePath
    };

    const dropbox = await this.getDropboxInstance();
    return (await dropbox.filesDeleteV2(args)).result;
  }

  async downloadFile(filePath: string): Promise<files.FileMetadata> {
    const args: files.DownloadArg = {
      path: filePath
    };

    const dropbox = await this.getDropboxInstance();
    return (await dropbox.filesDownload(args)).result as any;
  }

  async uploadFile(file: object, autorename: boolean, filePath: string, mode?: DropboxUploadMode): Promise<files.FileMetadata> {
    let args: files.UploadArg = {
      contents: file,
      autorename,
      path: filePath,
    };

    if (mode) {
      const writeMode: files.WriteMode = {
        '.tag': 'overwrite'
      };

      args = {
        contents: file,
        autorename,
        path: filePath,
        mode: writeMode
      };
    }

    const dropbox = await this.getDropboxInstance();
    return (await dropbox.filesUpload(args)).result;
  }

  async getMetaData(folderName: string)
    : Promise<files.FileMetadataReference | files.FolderMetadataReference | files.DeletedMetadataReference> {
    const args: files.GetMetadataArg = {
      path: `/${folderName}`
    };

    const dropbox = await this.getDropboxInstance();
    return (await dropbox.filesGetMetadata(args)).result as any;
  }

  private async getDropboxInstance(): Promise<Dropbox> {
    const token = await this.getToken();
    if ((!this.dbx && token) || (this.dbx && token && this.currentToken !== token)) {
      this.dbx = new Dropbox({ accessToken: token });
      this.currentToken = token;
    }
    return this.dbx;
  }

  public async getToken(): Promise<string> {
    let authInfo = this.localStorage.getWithExpiry<DropboxAuthInfo>(this.DROPBOX_INFO_KEY);

    if (authInfo && authInfo.access_token) {
      return authInfo.access_token;
    }

    try{
      await this.authSvc.authenticateDropbox();
    }catch(err){
      console.log(err, 'err');
    }
    authInfo = this.localStorage.getWithExpiry<DropboxAuthInfo>(this.DROPBOX_INFO_KEY);
    if (authInfo && authInfo.access_token) {
      return authInfo.access_token;
    }
    throw new Error('Unable to retrieve auth info');
  }

  public isConnected(){
    let authInfo = this.localStorage.getWithExpiry<DropboxAuthInfo>(this.DROPBOX_INFO_KEY);
    if (authInfo && authInfo.access_token) {
      return true;
    }
    return false;
  }

  public unAttachDrive() {
    this.cloudRecentFilesService.clearCloudRecentFiles(CloudServiceEnum.DropBox);
    this.localStorage.remove(this.DROPBOX_INFO_KEY);
  }
}
